import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Mail, Phone, MapPin, Clock, Send, Check,
  Instagram, MessageCircle, CreditCard, Truck, Shield, FileText
} from 'lucide-react';
import { artistInfo } from '../data/artworksData';

const ContactPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: 'purchase',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const interests = [
    { id: 'purchase', label: { ru: 'Покупка оригинала', en: 'Purchase Original' } },
    { id: 'commission', label: { ru: 'Заказать работу', en: 'Commission a Work' } },
    { id: 'visit', label: { ru: 'Посещение галереи', en: 'Gallery Visit' } },
    { id: 'collaboration', label: { ru: 'Сотрудничество', en: 'Collaboration' } },
    { id: 'press', label: { ru: 'Пресса / Медиа', en: 'Press / Media' } },
    { id: 'other', label: { ru: 'Другое', en: 'Other' } }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gallery-bg pt-20">
      {/* Header */}
      <div className="bg-white border-b border-gallery-border">
        <div className="container mx-auto px-4 py-16 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h1 className="font-serif text-4xl md:text-5xl text-gallery-text mb-4">
              {lang === 'ru' ? 'Свяжитесь с нами' : 'Get in Touch'}
            </h1>
            <p className="text-gallery-muted">
              {lang === 'ru'
                ? 'Мы рады ответить на ваши вопросы о работах, условиях приобретения и сотрудничестве.'
                : 'We are happy to answer your questions about artworks, acquisition terms and collaboration.'
              }
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="font-serif text-2xl text-gallery-text mb-8">
              {lang === 'ru' ? 'Отправить сообщение' : 'Send a Message'}
            </h2>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white p-12 text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="text-green-600" size={32} />
                </div>
                <h3 className="font-serif text-2xl text-gallery-text mb-4">
                  {lang === 'ru' ? 'Сообщение отправлено!' : 'Message Sent!'}
                </h3>
                <p className="text-gallery-muted mb-6">
                  {lang === 'ru'
                    ? 'Мы свяжемся с вами в ближайшее время.'
                    : 'We will contact you shortly.'
                  }
                </p>
                <button
                  onClick={() => {
                    setIsSubmitted(false);
                    setFormData({ name: '', email: '', phone: '', interest: 'purchase', message: '' });
                  }}
                  className="text-gallery-accent hover:underline"
                >
                  {lang === 'ru' ? 'Отправить ещё' : 'Send Another'}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gallery-text mb-2">
                      {lang === 'ru' ? 'Имя' : 'Name'} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gallery-border focus:outline-none focus:border-gallery-accent transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gallery-text mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gallery-border focus:outline-none focus:border-gallery-accent transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gallery-text mb-2">
                      {lang === 'ru' ? 'Телефон' : 'Phone'}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gallery-border focus:outline-none focus:border-gallery-accent transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gallery-text mb-2">
                      {lang === 'ru' ? 'Тема обращения' : 'Subject'} *
                    </label>
                    <select
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gallery-border focus:outline-none focus:border-gallery-accent transition-colors"
                    >
                      {interests.map(int => (
                        <option key={int.id} value={int.id}>
                          {int.label[lang]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gallery-text mb-2">
                    {lang === 'ru' ? 'Сообщение' : 'Message'} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-white border border-gallery-border focus:outline-none focus:border-gallery-accent transition-colors resize-none"
                    placeholder={lang === 'ru'
                      ? 'Расскажите подробнее о вашем запросе...'
                      : 'Tell us more about your inquiry...'
                    }
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-3 bg-gallery-text text-white py-4 text-sm tracking-wider uppercase hover:bg-gallery-accent transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {lang === 'ru' ? 'Отправка...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      {lang === 'ru' ? 'Отправить' : 'Send Message'}
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="font-serif text-2xl text-gallery-text mb-8">
              {lang === 'ru' ? 'Контактная информация' : 'Contact Information'}
            </h2>

            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gallery-light flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-gallery-accent" />
                </div>
                <div>
                  <h3 className="text-gallery-text font-medium mb-1">Email</h3>
                  <a href={`mailto:${artistInfo.email}`} className="text-gallery-muted hover:text-gallery-accent transition-colors">
                    {artistInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gallery-light flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-gallery-accent" />
                </div>
                <div>
                  <h3 className="text-gallery-text font-medium mb-1">
                    {lang === 'ru' ? 'Телефон' : 'Phone'}
                  </h3>
                  <a href={`tel:${artistInfo.phone}`} className="text-gallery-muted hover:text-gallery-accent transition-colors">
                    {artistInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gallery-light flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-gallery-accent" />
                </div>
                <div>
                  <h3 className="text-gallery-text font-medium mb-1">
                    {lang === 'ru' ? 'Локация' : 'Location'}
                  </h3>
                  <p className="text-gallery-muted">
                    {artistInfo.location[lang]}
                  </p>
                  <p className="text-gallery-muted text-sm mt-1">
                    {lang === 'ru' ? 'Просмотр по предварительной записи' : 'Viewing by appointment'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gallery-light flex items-center justify-center flex-shrink-0">
                  <Clock size={20} className="text-gallery-accent" />
                </div>
                <div>
                  <h3 className="text-gallery-text font-medium mb-1">
                    {lang === 'ru' ? 'Время ответа' : 'Response Time'}
                  </h3>
                  <p className="text-gallery-muted">
                    {lang === 'ru' ? 'В течение 24 часов' : 'Within 24 hours'}
                  </p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="mb-12">
              <h3 className="text-gallery-text font-medium mb-4">
                {lang === 'ru' ? 'Социальные сети' : 'Social Media'}
              </h3>
              <div className="flex gap-4">
                <a
                  href={artistInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gallery-light flex items-center justify-center hover:bg-gallery-accent hover:text-white transition-colors"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href={artistInfo.social.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gallery-light flex items-center justify-center hover:bg-gallery-accent hover:text-white transition-colors"
                >
                  <MessageCircle size={20} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Purchase Info Section */}
      <div className="bg-white border-t border-gallery-border">
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl text-gallery-text mb-4">
              {lang === 'ru' ? 'Условия приобретения' : 'Purchase Terms'}
            </h2>
            <p className="text-gallery-muted max-w-2xl mx-auto">
              {lang === 'ru'
                ? 'Мы стремимся сделать процесс приобретения произведений искусства максимально простым и безопасным.'
                : 'We strive to make the art acquisition process as simple and secure as possible.'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: CreditCard,
                title: { ru: 'Оплата', en: 'Payment' },
                description: {
                  ru: 'Банковский перевод, PayPal. Возможна рассрочка для работ от 5 000 EUR.',
                  en: 'Bank transfer, PayPal. Installment plans available for works over 5,000 EUR.'
                }
              },
              {
                icon: Truck,
                title: { ru: 'Доставка', en: 'Shipping' },
                description: {
                  ru: 'Профессиональная упаковка. Доставка по всему миру с полным страхованием.',
                  en: 'Professional packaging. Worldwide shipping with full insurance.'
                }
              },
              {
                icon: Shield,
                title: { ru: 'Гарантии', en: 'Guarantees' },
                description: {
                  ru: 'Сертификат подлинности с каждой работой. 14 дней на возврат.',
                  en: 'Certificate of authenticity with each work. 14-day return policy.'
                }
              },
              {
                icon: FileText,
                title: { ru: 'Документы', en: 'Documentation' },
                description: {
                  ru: 'Полный пакет документов для таможни и коллекционных целей.',
                  en: 'Complete documentation package for customs and collection purposes.'
                }
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-6 bg-gallery-bg"
              >
                <item.icon className="text-gallery-accent mb-4" size={28} />
                <h3 className="font-serif text-lg text-gallery-text mb-2">
                  {item.title[lang]}
                </h3>
                <p className="text-gallery-muted text-sm">
                  {item.description[lang]}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
